'use client';

import { ThemeFonts } from '@common/atoms/ThemeFonts/ThemeFonts.client';
import { ThemeVariables } from '@common/atoms/ThemeVariables/ThemeVariables';
import { decodeContextHash } from '@common/defaults/ContextHash';
import { getTheme, getThemeClass } from '@common/defaults/getTheme';
import { useErrorLogger } from '@common/hooks/useErrorLogger';
import type { NextError } from '@common/types/NextError';
import { DefaultErrorView } from '@web/templates/ErrorPages/DefaultErrorView';
import { getDummyContextHashFromHost } from '@web/utils/getDummyContextHashFromHost';

import '@web/styles/global.scss';

interface Props {
    error: NextError;
    unstable_retry: () => void;
}

/**
 * global-error.tsx is a client-side component and should not rely
 * on any API requests to make sure it can be rendered in all cases.
 */
const GlobalError = (props?: Props) => {
    const host = typeof window !== 'undefined' ? window.location.host : 'unknown';
    const contextHashString = getDummyContextHashFromHost({ host });

    useErrorLogger(props?.error, 'global-error');

    if (!contextHashString) {
        return;
    }

    const contextHash = decodeContextHash(contextHashString);
    const { locale, platformID } = contextHash;
    const themeID = getTheme(host);

    return (
        <html lang={locale.replace('_', '-')} className={`theme ${getThemeClass(themeID)}`}>
            <head>
                <ThemeFonts themeID={themeID} />
                <ThemeVariables contextData={undefined} themeID={themeID} />
            </head>
            <body>
                <DefaultErrorView localeID={locale} platformID={platformID} />
            </body>
        </html>
    );
};

export default GlobalError;
